import { Mail } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useInviteUser } from "@/lib/api";
import { logger } from "@/lib/logger";

const ALLOWED_DOMAIN = "boardofinnovation.com";

interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteUserDialog({ open, onOpenChange }: InviteUserDialogProps) {
  const inviteUser = useInviteUser();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const sending = inviteUser.isPending;

  const reset = () => {
    setEmail("");
    setError("");
    setSuccessMsg("");
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      reset();
    }
    onOpenChange(next);
  };

  const handleInvite = async () => {
    setError("");
    setSuccessMsg("");

    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError("Please enter an email address.");
      return;
    }

    const domain = trimmed.split("@")[1] ?? "";
    if (domain !== ALLOWED_DOMAIN) {
      setError(`Only @${ALLOWED_DOMAIN} email addresses can be invited.`);
      return;
    }

    try {
      const result = await inviteUser.mutateAsync({ email: trimmed });
      setSuccessMsg(result?.message ?? "Pre-authorized.");
      setEmail("");
    } catch (err) {
      logger.error("[invite] failed:", err);
      const body = (err as { body?: { detail?: string } })?.body;
      setError(body?.detail ?? "Failed to pre-authorize. Please try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite a Colleague</DialogTitle>
          <DialogDescription>
            Pre-authorize this person &mdash; they&apos;ll get access the first time they sign in
            with Microsoft. Only @{ALLOWED_DOMAIN} addresses are accepted.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="invite-email">Email address</Label>
              <div className="relative">
                <Mail className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                <Input
                  id="invite-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={`name@${ALLOWED_DOMAIN}`}
                  className="pl-9"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleInvite();
                    }
                  }}
                />
              </div>
            </div>

            {error && <p className="text-destructive text-sm">{error}</p>}
            {successMsg && (
              <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700 dark:bg-green-950 dark:text-green-300">
                {successMsg}
              </p>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={sending}>
              Cancel
            </Button>
            <Button onClick={handleInvite} disabled={sending}>
              {sending ? "Adding..." : "Pre-authorize"}
            </Button>
          </DialogFooter>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
